/**
 * The fine ground, drawn (decision 11, WORKBOARD F1 — "drawing → lane E"): a local patch of
 * person-scale micro-relief over the planet mesh around the camera, so what a person stands on,
 * hides behind and shoots over is what you see. Every vertex sits exactly on the sim's person
 * ground (`personSurfaceRadius`: the mesh plus `fineOffset`), so at the vertices drawn and
 * simulated agree; between them the patch interpolates at half a person-height, finer than the
 * micro-relief's small octave (two person-heights).
 *
 * **It grows in without a pop (67.29, the owner: "fine terrain has a render distance snap in problem").** The patch
 * used to be rebuilt round the focus every sixth of its size and swapped in whole, its square's outer ring tapered to
 * the mesh — so the fine edge crept toward the camera and jumped back at each swap, the whole patch resampled at a new
 * offset, and the swap frame recomputed every normal (5–10 ms). Now:
 * - **the fade follows the focus, every frame, in the shader:** each vertex carries the mesh's point and the fine
 *   ground's lift above it; the vertex shader lifts it by the fade — full fine within FADE_IN of the focus, the mesh
 *   past FADE_OUT, a smooth step between — so the fine ground grows in continuously as the camera travels;
 * - **the lattice is the world's:** vertices sit on a grid fixed to an anchor on the planet (re-anchored only after a
 *   long way), so a rebuild only adds rows ahead and drops rows behind, and every vertex two builds share is the same;
 * - **the normals are built within the frame budget** with the rows (central differences, the mesh's and the fine
 *   ground's, blended by the same fade), so a swap is a copy;
 * - the patch's own square edge still fades to the mesh — a net, should the focus ever outrun a rebuild;
 * - **71.3 (b): travel scrolls it** (as a clipmap does): the kept cells move, only the rows and columns new to the square
 *   are filled, in the frame (~1 ms for a shift's six rows in the game's patch; a whole build is ~13 ms over five frames).
 *   A whole build, time-sliced, is left for a new planet, a crater, a dig, the props' discs and a re-anchor.
 *
 * Look: each vertex takes the live colour of the planet facet under it (the planet mesh's colour attribute — craters
 * and fog included), shaded a touch lighter on hummock tops and darker in the hollows as the fade brings the relief in,
 * so the relief reads under flat light. Polygon offset wins the depth ties where the relief is zero.
 *
 * Cost: a full patch is ~6,500 ground lookups at the default size (~11 ms), so a rebuild is time-sliced — a few rows per
 * frame inside a budget, into a second buffer swapped in whole. Off (and drawing nothing) when the profile's fine
 * amplitude is 0 (classic). Presentation only: reads state, writes none.
 *
 * **Only switch it on where the sim reads the fine ground** (walk, bullets, sight — lane C's
 * consumers of core/fineTerrain.ts): drawn hummocks under a walk that stands on the mesh bury
 * people's feet.
 */
import { applyTopoOverlay } from './topoOverlay';
import * as THREE from 'three';
import type { PlanetState, Vec3 } from '../types';
import { PERSON_BODY_HEIGHT, type PersonSet } from '../core/scaleProfile';
import { groundAt, fineOffset, digHeightAt, craterletHeightAt, type GroundDials } from '../core/fineTerrain';
/** 71 step 0: the mesh alone — the one ground's coarse level (the dials don't reach it). */
const COARSE: GroundDials = { fineAmplitude: 0, fineWavelength: 1, k: 1 };
import { standGround } from '../core/standGround';
import { terraceGround } from '../core/terraceGround';
import { nearestSurfaceVertex } from '../core/surface';
import { flatWeight, type FlatDisc } from './fineFlatten';
import { FINE_FADE, GROUND_LIFT, meshLift } from './groundLift';

/**
 * The switch: the game draws the fine ground only once the sim stands people on it (lane C's
 * walk, bullets and sight reading core/fineTerrain.ts). On since S3 (lane C): walking, landing,
 * bullets and sight all read the fine ground at person scale.
 */
export const FINE_GROUND_DRAWN = true;
/** The Lab's override (decision 123): true / false wins over the URL and the constant; null defers to them. */
let drawnOverride: boolean | null = null;
export function setFineGroundDrawn(v: boolean | null): void { drawnOverride = v; }
/**
 * The person set as the patch reads it: the relief's amplitude and wavelength, and `k` (63.11) — with it the patch lays the
 * digs and the craterlets into its ground, as the sim's `personSurfaceRadius` does (without it, the relief alone).
 */
export type PatchPersonSet = GroundDials;

/**
 * 63.11: how much a bowl darkens the patch's ground (a dig's or a craterlet's): × (1 − `shade` × its depth's share of
 * `fullAt` person heights, at most 1) — the turned earth reads where the lattice (a vertex a person apart) is too coarse to
 * shape a grenade's bowl. Presentation only.
 */
export const BOWL_SHADE = { shade: 0.4, fullAt: 0.2 };

export function fineGroundDrawn(): boolean {
  if (drawnOverride !== null) return drawnOverride;
  if (FINE_GROUND_DRAWN) return true;
  try { return typeof location !== 'undefined' && new URLSearchParams(location.search).get('fineground') === '1'; } catch { return false; }
}

/**
 * 67.29: the fade and the rebuild, as shares of the patch's half-size. The fine ground is whole within FADE_IN of the
 * focus and gone past FADE_OUT; a rebuild starts when the focus has moved REBUILD of the half-size along either axis;
 * the square's own edge fades over its outer EDGE. No pop needs FADE_OUT + REBUILD + a rebuild's travel inside 1 − EDGE
 * (a point the fade reaches must be whole in both the old patch and the new at the swap): in the game's patch (12
 * wavelengths, 3 ms a frame, ~5 frames a rebuild) that leaves 0.18 of the half-size (13 people) for 0.08 of rebuild and a
 * build's travel — about eight times a run's. The patch's size is the fine ground's reach: whole to 0.4 of it, gone by 3/4.
 *
 * 67.50 (3) (the owner: "fine detail ring is still visible and transitions are visible"): the game's patch went from 6
 * wavelengths at 12 vertices each to 12 at 6 (the same 145² vertices and build; the lattice misses the relief by at most
 * 0.05 people, p99 0.03), and the fade from 0.5–0.74 to 0.4–0.74: the fine ground whole to 29 people and gone by 53 (it
 * was 18 and 27) — just inside the horizon from the shoulder camera (59 people on the Lab's planets; the haze is too thin
 * there to hide it: 6 % at 53), over a band near three times as wide — and it fades to the far ground's lift
 * (rendering/groundLift.ts), the relief's crest, so a ridge never drops below what the sim's would hide as it fades.
 */
/** (71.3 (b): `scrollMax` — the most a scroll moves, in shifts (REBUILD each); a longer jump builds the patch whole.) */
export const FINE_PATCH = { fadeIn: 0.4, fadeOut: 0.74, rebuild: 1 / 12, edge: 0.08, reanchorRadians: 0.25, scrollMax: 3 };
/**
 * 62.61 / 67.50, the owner on the commander view's terrain pop: "Fade." The patch fades out with the camera's height over
 * its ground: whole below `fromPeople`, gone by `toPeople`. It isn't rebuilt while gone, and its relief, the far lift and
 * everything riding the drawn ground (the scatter, the tufts, people's feet) fade by the same share (FINE_FADE.uFineOn).
 * Presentation only.
 */
export const FINE_HEIGHT_FADE = { fromPeople: 60, toPeople: 120 };
/** The patch's share at a camera `height` (world) over its ground: 1 low, 0 high, a smoothstep between. */
export function fineHeightFade(height: number, person: number): number {
  const a = FINE_HEIGHT_FADE.fromPeople * person, b = FINE_HEIGHT_FADE.toPeople * person;
  const t = Math.min(1, Math.max(0, (height - a) / (b - a)));
  return 1 - t * t * (3 - 2 * t);
}

export interface FinePatchOptions {
  /** Half the patch's side, in fine wavelengths (the broad octave, 6 person-heights): 3.5 ≈ ±21 heights. */
  halfSizeWavelengths?: number;
  /** Vertices per fine wavelength: 12 = every half person-height. */
  perWavelength?: number;
  /** Milliseconds of rebuild per frame. */
  budgetMs?: number;
}

/** The lattice's frame: an anchor direction and its tangent axes; a vertex is normalize(c + t1·i·step + t2·j·step). */
interface Anchor { planetId: number; c: THREE.Vector3; t1: THREE.Vector3; t2: THREE.Vector3; step: number }

/** A patch's cells (n × n): what the geometry draws, and what a scroll keeps of them (71.3 (b)). */
interface Grid {
  pos: Float32Array;        // the mesh's points, relative to `origin`
  fine: Float32Array;       // the fine ground's points, relative to `origin` (for its normals)
  lift: Float32Array; edge: Float32Array; col: Float32Array;
  nrm: Float32Array; nrmCoarse: Float32Array;
  vtx: Int32Array;          // 71.3 (b): the planet mesh's vertex under each cell (its live colour, refreshed at every scroll)
  dark: Float32Array;       // 71.3 (b): each cell's bowl shade (63.11: 1, or darker in a dig's or a craterlet's bowl)
}

interface Build extends Grid {
  planet: PlanetState;
  anchor: Anchor;
  ic: number; jc: number;   // the lattice point at the patch's centre
  row: number;              // rows of positions done; then rows of normals (row − n)
  origin: THREE.Vector3;    // world position the vertices are relative to (float precision)
  // 67.50 (1): the relief's share kept at each vertex (0 under a prop: rendering/fineFlatten.ts), and the discs it is from
  mask: Float32Array; floor: Float32Array; discs: readonly FlatDisc[] | null;
}

const _d = new THREE.Vector3();
const _cell = { x: 0, y: 0, z: 0 };

/** 67.50 (1): the patch's fade in world space, written by the patch each frame (now kept in rendering/groundLift.ts). */
export { FINE_FADE } from './groundLift';

/** The fade's weight at distance `d` (the shader's smoothstep, mirrored). */
function fade(d: number, inner: number, outer: number): number {
  const t = Math.min(1, Math.max(0, (d - inner) / (outer - inner)));
  return 1 - t * t * (3 - 2 * t);
}

/** Two props' disc lists equal by content (the same array, or the same discs in the same order). */
function sameDiscs(a: readonly FlatDisc[] | null, b: readonly FlatDisc[] | null): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let k = 0; k < a.length; k++) {
    const p = a[k], q = b[k];
    if (p.r !== q.r || p.dir.x !== q.dir.x || p.dir.y !== q.dir.y || p.dir.z !== q.dir.z || (p.lift ?? 0) !== (q.lift ?? 0)) return false;
  }
  return true;
}


export class FineTerrainPatch {
  readonly mesh: THREE.Mesh;
  /** Milliseconds the last finished rebuild took in total, and how many frames it spread over. */
  lastBuild = { ms: 0, frames: 0 };
  /** 71.3 (b): the last scroll's milliseconds and the cells it filled. */
  lastScroll = { ms: 0, cells: 0 };
  private readonly n: number;
  private readonly h: number;
  private readonly halfW: number;
  private readonly perWavelength: number;
  private readonly budget: number;
  private readonly edgeSteps: number;
  private readonly shiftSteps: number;
  private anchor: Anchor | null = null;
  private pending: Build | null = null;
  /** a build's buffers, and (71.3 (b)) the drawn patch's own: a finished build or a scroll swaps the two */
  private buffers: Grid | null = null;
  private kept: Grid | null = null;
  /** 71.3 (b): the cells a scroll filled (1) — the rest are the kept patch's, moved */
  private fresh: Uint8Array | null = null;
  private pendingMs = 0; private pendingFrames = 0;
  private built: { planetId: number; heightmap: PlanetState['heightmap']; anchor: Anchor; ic: number; jc: number; amplitude: number; origin: THREE.Vector3; mask: Float32Array; floor: Float32Array; discs: readonly FlatDisc[] | null; craterlets?: PlanetState['craterlets']; digs?: PlanetState['digs'] } | null = null;
  /** 67.50 (1): the props' discs on a planet (the host's: rendering/fineFlatten.ts flatDiscs over the state), or none. */
  private discsFor: ((planet: PlanetState) => readonly FlatDisc[]) | null = null;
  private flatMargin = 0;
  /** 67.50 (1): the props' discs, and the margin (world units) the relief comes back over past each. */
  setFlatDiscs(fn: ((planet: PlanetState) => readonly FlatDisc[]) | null, margin = 0): void { this.discsFor = fn; this.flatMargin = margin; }
  /** The focus's ground point (world) this frame — the fade's centre, for the shader and drawnGround alike. */
  private readonly focus = new THREE.Vector3();
  /** the owner's "Fade.": the patch's share by the camera's height (1 low, 0 high) */
  private heightFade = 1;
  private readonly uniforms = {
    uFocus: { value: new THREE.Vector3() },   // the focus, relative to the drawn patch's origin
    uRadial: { value: new THREE.Vector3() },  // the drawn patch's origin, relative to the planet's centre
    uFade: { value: new THREE.Vector2(0, 1) }, // world distances: whole within x, gone past y
    uAmp: { value: 1 },
  };

  constructor(private readonly scene: THREE.Scene, opts: FinePatchOptions = {}) {
    this.halfW = opts.halfSizeWavelengths ?? 3.5;
    this.perWavelength = opts.perWavelength ?? 12;
    this.n = Math.round(2 * this.halfW * this.perWavelength) + 1;
    this.h = (this.n - 1) / 2;
    this.budget = opts.budgetMs ?? 3;
    this.edgeSteps = Math.max(1, FINE_PATCH.edge * this.h);
    this.shiftSteps = Math.max(1, Math.round(FINE_PATCH.rebuild * this.h));
    const n = this.n, idx: number[] = [];
    for (let i = 0; i < n - 1; i++) for (let j = 0; j < n - 1; j++) {
      const a = i * n + j, b = a + 1, c = a + n, d = c + 1;
      idx.push(a, c, b, b, c, d);
    }
    const geo = new THREE.BufferGeometry();
    geo.setIndex(new THREE.BufferAttribute(new Uint32Array(idx), 1));
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * n * 3), 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(n * n * 3), 3));
    geo.setAttribute('normalCoarse', new THREE.BufferAttribute(new Float32Array(n * n * 3), 3));
    geo.setAttribute('fineLift', new THREE.BufferAttribute(new Float32Array(n * n), 1));
    geo.setAttribute('edgeK', new THREE.BufferAttribute(new Float32Array(n * n), 1));
    geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(n * n * 3), 3));
    const material = new THREE.MeshLambertMaterial({
      // smooth: the patch carries vertex normals; flat shading ignored them and lit each grid triangle by its own
      // facet, which on a slope alternates light and dark down the diagonal — a checkerboard round the player (the
      // visual tour, Oct 10). The hummocks are soft forms; the planet's facets stay flat beyond the fade.
      vertexColors: true, flatShading: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
      // 67.39: blended over the planet by the fade (see the shader), so where it fades out the planet's own faceted
      // ground shows through — no seam where the patch's smooth shading met the flat facets, no square edge
      transparent: true,
    });
    const u = this.uniforms;
    material.onBeforeCompile = shader => {
      Object.assign(shader.uniforms, u, GROUND_LIFT, { uFineOn: FINE_FADE.uFineOn });
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>
uniform float uGroundLift;
uniform float uFineOn;
uniform vec3 uFocus;
uniform vec3 uRadial;
uniform vec2 uFade;
uniform float uAmp;
attribute float fineLift;
attribute float edgeK;
attribute vec3 normalCoarse;
varying float vFineFade;
float fineFade() { return uFineOn * edgeK * (1.0 - smoothstep(uFade.x, uFade.y, distance(position, uFocus))); }`)   // (× on: the height fade, FINE_HEIGHT_FADE)
        .replace('#include <color_vertex>', `#include <color_vertex>
#ifdef USE_COLOR
  { float t = fineFade(); t *= t; vColor.xyz *= 0.9 + 0.18 * (uAmp > 0.0 ? fineLift / uAmp : 0.0) * t + 0.1 * (1.0 - t); }
#endif`)
        // 67.39: the patch's cover is the fade (vFineFade, its opacity); the relief and its normals rise with its square,
        // so the hummocks come up only where the patch is mostly over the planet — no band of half-risen bumps
        .replace('#include <beginnormal_vertex>', '{ float t0 = fineFade(); vFineFade = t0; }\nvec3 objectNormal = normalize(mix(normalCoarse, normal, vFineFade * vFineFade));')
        .replace('#include <begin_vertex>', 'vec3 transformed = position + normalize(position + uRadial) * (uGroundLift * uFineOn * (1.0 - vFineFade * vFineFade) + fineLift * vFineFade * vFineFade);');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>
varying float vFineFade;`)
        .replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>
  if (vFineFade < 0.004) discard;   // 67.39: past the fade the planet's own ground, drawn beneath`)
        .replace('#include <color_fragment>', `#include <color_fragment>
  diffuseColor.a *= vFineFade;`);
    };
    applyTopoOverlay(material);   // 68.17: the commander's topo overlay on the fine ground too
    material.customProgramCacheKey = () => 'fine-ground-fade';
    this.mesh = new THREE.Mesh(geo, material);
    this.mesh.visible = false;
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -1;   // 67.39: first of the see-through things, so ground decals and smoke still draw over it
    this.mesh.raycast = () => {};
    scene.add(this.mesh);
  }

  /**
   * Keep the patch around `focusDir` on `planet`. `colors` is the planet mesh's live colour
   * attribute (null: a neutral ground). Moves the fade to the focus every call; starts a rebuild
   * when the focus has moved REBUILD of the patch along its lattice, the planet changed, or a
   * crater changed its mesh; advances it within the budget.
   */
  /** (`budgetMs`: this frame's slice of the rebuild — 62.61's ledger claims it; the patch's own when absent) */
  update(planet: PlanetState, focusDir: Vec3, ps: PatchPersonSet, colors: Float32Array | null, budgetMs?: number,
    /** the owner's "Fade.": the patch's share by the camera's height (fineHeightFade) — 0: hidden and not rebuilt */
    heightFade = 1): void {
    if (!(ps.fineAmplitude > 0)) { this.mesh.visible = false; this.pending = null; this.built = null; FINE_FADE.uFineOn.value = 0; return; }
    this.heightFade = Math.min(1, Math.max(0, heightFade));
    if (this.heightFade <= 0) { this.mesh.visible = false; FINE_FADE.uFineOn.value = 0; return; }   // (its last build kept: back down, it is current or rebuilt)
    if (this.built) this.mesh.visible = true;
    _d.set(focusDir.x, focusDir.y, focusDir.z).normalize();
    this.setFocus(planet, _d, ps);
    const anchor = this.anchorFor(planet, _d, ps);
    const { ic, jc } = this.latticeOf(anchor, _d);
    const b = this.built, discs = this.discsFor?.(planet) ?? null;
    const far = (x: { anchor: Anchor; ic: number; jc: number }, steps: number) => x.anchor !== anchor || Math.max(Math.abs(x.ic - ic), Math.abs(x.jc - jc)) > steps;
    // (the discs by content: a crewed gun pit is replaced in its walk-table slot every tick — its reload, its turret — so the
    // host's discs come back a new array of the same discs every frame, and by identity a seated gunner's patch restarted
    // its build every frame and never finished it: 3 ms a frame at the pit, lane A's profile)
    // (63.11: a new craterlet or dig re-lays the patch, as a crater's heightmap does — its relief is the fine ground's; a dig
    // changes its list a quarter of its progress at a time, so a few re-lays a dig)
    const changed = !b || b.planetId !== planet.id || b.heightmap !== planet.heightmap || b.craterlets !== planet.craterlets || b.digs !== planet.digs || b.amplitude !== ps.fineAmplitude || !sameDiscs(b.discs, discs) || b.anchor !== anchor;
    const moved = !!b && far(b, this.shiftSteps);
    // 71.3 (b): travel alone scrolls the drawn patch in the frame (a few rows: FINE_PATCH.scrollMax shifts at most); a new
    // ground, a new anchor or a long jump builds a whole patch, time-sliced, as before
    if (!changed && moved && !this.pending && this.kept && Math.max(Math.abs(ic - b!.ic), Math.abs(jc - b!.jc)) <= FINE_PATCH.scrollMax * this.shiftSteps) this.scroll(planet, ic, jc, ps, colors);
    else if ((changed || moved) && (!this.pending || this.pending.planet.id !== planet.id || this.pending.planet.heightmap !== planet.heightmap || this.pending.planet.craterlets !== planet.craterlets || this.pending.planet.digs !== planet.digs || !sameDiscs(this.pending.discs, discs) || far(this.pending, 2 * this.shiftSteps))) this.start(planet, anchor, ic, jc);
    if (this.pending) this.advance(ps, colors, budgetMs ?? this.budget);
    this.syncUniforms(planet, ps);
  }

  /** Build the whole patch now (tests, sheets). */
  buildNow(planet: PlanetState, focusDir: Vec3, ps: PatchPersonSet, colors: Float32Array | null): void {
    if (!(ps.fineAmplitude > 0)) { this.mesh.visible = false; return; }
    const f = new THREE.Vector3(focusDir.x, focusDir.y, focusDir.z).normalize();
    this.setFocus(planet, f, ps);
    const anchor = this.anchorFor(planet, f, ps);
    const { ic, jc } = this.latticeOf(anchor, f);
    this.start(planet, anchor, ic, jc);
    while (this.pending) this.advance(ps, colors, Infinity);
    this.syncUniforms(planet, ps);
  }

  /** The lattice's anchor on this planet: kept while the focus stays within REANCHOR of it (a new one past that). */
  private anchorFor(planet: PlanetState, f: THREE.Vector3, ps: Pick<PersonSet, 'fineWavelength'>): Anchor {
    const step = ps.fineWavelength / this.perWavelength / planet.radius;
    const a = this.anchor;
    if (a && a.planetId === planet.id && a.step === step && a.c.dot(f) > Math.cos(FINE_PATCH.reanchorRadians)) return a;
    const ref = Math.abs(f.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const t1 = new THREE.Vector3().crossVectors(f, ref).normalize();
    const t2 = new THREE.Vector3().crossVectors(f, t1);
    this.anchor = { planetId: planet.id, c: f.clone(), t1, t2, step };
    return this.anchor;
  }

  /** The lattice point nearest a direction (gnomonic coordinates in steps, rounded). */
  private latticeOf(a: Anchor, f: THREE.Vector3): { ic: number; jc: number } {
    const along = f.dot(a.c);
    return { ic: Math.round(f.dot(a.t1) / along / a.step), jc: Math.round(f.dot(a.t2) / along / a.step) };
  }

  private setFocus(planet: PlanetState, f: THREE.Vector3, ps: Pick<PersonSet, 'fineWavelength'>): void {
    const r = groundAt(planet, f, 'coarse', COARSE);
    this.focus.set(planet.position.x + f.x * r, planet.position.y + f.y * r, planet.position.z + f.z * r);
  }

  /** The fade's distances (world), from the patch's half-size. */
  private fadeRadii(ps: Pick<PersonSet, 'fineWavelength'>): { inner: number; outer: number } {
    const half = this.h * (ps.fineWavelength / this.perWavelength);   // the lattice's step × R is a vertex spacing on the ground
    return { inner: FINE_PATCH.fadeIn * half, outer: FINE_PATCH.fadeOut * half };
  }

  private syncUniforms(planet: PlanetState, ps: GroundDials): void {
    const b = this.built;
    if (!b) return;
    const u = this.uniforms;
    u.uFocus.value.copy(this.focus).sub(b.origin);
    u.uRadial.value.set(b.origin.x - planet.position.x, b.origin.y - planet.position.y, b.origin.z - planet.position.z);
    const { inner, outer } = this.fadeRadii(ps);
    u.uFade.value.set(inner, outer);
    FINE_FADE.uFineFocus.value.copy(this.focus); FINE_FADE.uFineFade.value.set(inner, outer); FINE_FADE.uFineOn.value = this.mesh.visible ? this.heightFade : 0;
    u.uAmp.value = ps.fineAmplitude;
  }

  private start(planet: PlanetState, anchor: Anchor, ic: number, jc: number): void {
    const n = this.n;
    const c = new THREE.Vector3().copy(anchor.c).addScaledVector(anchor.t1, ic * anchor.step).addScaledVector(anchor.t2, jc * anchor.step).normalize();
    const r0 = groundAt(planet, c, 'coarse', COARSE);
    const origin = c.clone().multiplyScalar(r0).add(new THREE.Vector3(planet.position.x, planet.position.y, planet.position.z));
    // one set of build buffers, reused (a swap copies them into the geometry): ~2 MB a build left to the collector
    // showed as 5–7 ms frames
    this.buffers ??= this.grid();
    const discs = this.discsFor?.(planet) ?? null;
    const flat = this.flatMask(planet, anchor, ic, jc, discs);
    this.pending = { planet, anchor, ic, jc, row: 0, origin, ...this.buffers, mask: flat.mask, floor: flat.floor, discs };
    this.pendingMs = 0; this.pendingFrames = 0;
  }

  private grid(): Grid {
    const n = this.n;
    return {
      pos: new Float32Array(n * n * 3), fine: new Float32Array(n * n * 3), lift: new Float32Array(n * n), edge: new Float32Array(n * n),
      col: new Float32Array(n * n * 3), nrm: new Float32Array(n * n * 3), nrmCoarse: new Float32Array(n * n * 3),
      vtx: new Int32Array(n * n), dark: new Float32Array(n * n),
    };
  }

  private advance(ps: PatchPersonSet, colors: Float32Array | null, budget = this.budget): void {
    const job = this.pending!;
    const t0 = performance.now();
    const n = this.n;
    // at least one row a frame, then rows while the budget lasts (a tiny budget still makes progress): n rows of points,
    // then n rows of normals
    for (let first = true; job.row < 2 * n && (first || performance.now() - t0 < budget); first = false) {
      if (job.row >= n) { this.normalsRow(job, job.origin, job.planet, job.row - n); job.row++; continue; }
      for (let j = 0; j < n; j++) this.fillCell(job, job, job.row, j, job.ic, job.jc, job.mask, job.floor, ps, colors);
      job.row++;
    }
    this.pendingMs += performance.now() - t0;
    this.pendingFrames++;
    if (job.row >= 2 * n) this.swapIn(job, ps.fineAmplitude);
  }


  /** One cell (i, j) of a patch centred on lattice point (ic, jc): the mesh's point, the fine ground's lift, its colour. */
  private fillCell(g: Grid, at: { planet: PlanetState; anchor: Anchor; origin: THREE.Vector3 }, i: number, j: number, ic: number, jc: number,
    mask: Float32Array, floor: Float32Array, ps: PatchPersonSet, colors: Float32Array | null): void {
    const n = this.n, h = this.h, P = at.planet, A = at.anchor, origin = at.origin, dir = _cell;
    const gi = (ic - h + i) * A.step, gj = (jc - h + j) * A.step;
    dir.x = A.c.x + A.t1.x * gi + A.t2.x * gj;
    dir.y = A.c.y + A.t1.y * gi + A.t2.y * gj;
    dir.z = A.c.z + A.t1.z * gi + A.t2.z * gj;
    const l = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z);
    dir.x /= l; dir.y /= l; dir.z /= l;
    const coarse = groundAt(P, dir, 'coarse', COARSE);                                    // the mesh
    const k = i * n + j, k3 = k * 3;
    // fine = coarse + relief = personSurfaceRadius(P, dir, ps); 67.50 (1): flattened under a rock or a plinth (its
    // mask) to the coarse surface, where the sim stands it. (67.50 (3): the shader adds the far lift × (1 − fade²) —
    // rendering/groundLift.ts — so where the relief is whole the vertex is the sim's ground, and never under the mesh)
    // (67.52: a seated build's disc flattened to its base — the relief's place taken by the disc's lift)
    // 63.11: the digs and the craterlets in it, as the sim's ground has them (personSurfaceRadius with the person's k) —
    // the feet, the eyes and sight already stood in bowls the patch did not draw
    const dug = (P.digs?.length ? digHeightAt(P, dir.x, dir.y, dir.z, ps.k) : 0) + (P.craterlets?.length ? craterletHeightAt(P, dir.x, dir.y, dir.z, ps.k) : 0);
    // 63.14 2b: the terraces in it too (core/terraceGround.ts: a pad's level, its batter band), as the sim's ground has them
    const relief = fineOffset(P, dir.x, dir.y, dir.z, ps.fineAmplitude, ps.fineWavelength) + dug;
    const fine = P.terraces?.length ? terraceGround(P.terraces, P.radius, dir.x, dir.y, dir.z, coarse + relief) - coarse : relief;
    const lift = fine * mask[k] + floor[k] * (1 - mask[k]);
    g.pos[k3] = P.position.x + dir.x * coarse - origin.x;
    g.pos[k3 + 1] = P.position.y + dir.y * coarse - origin.y;
    g.pos[k3 + 2] = P.position.z + dir.z * coarse - origin.z;
    g.fine[k3] = g.pos[k3] + dir.x * lift; g.fine[k3 + 1] = g.pos[k3 + 1] + dir.y * lift; g.fine[k3 + 2] = g.pos[k3 + 2] + dir.z * lift;
    g.lift[k] = lift;
    g.edge[k] = Math.min(1, Math.min(i, n - 1 - i, j, n - 1 - j) / this.edgeSteps);
    // colour: the facet's live colour (the shader shades it as the fade brings the relief in); 63.11: the turned earth of a
    // bowl, darker by its depth
    g.vtx[k] = colors ? nearestSurfaceVertex(P, dir) : -1;
    g.dark[k] = dug < 0 ? 1 - BOWL_SHADE.shade * Math.min(1, -dug / (BOWL_SHADE.fullAt * PERSON_BODY_HEIGHT * ps.k)) : 1;
    this.colourCell(g, k, colors);
  }

  /** A cell's colour: the planet facet's live colour under it (a neutral ground without), × its bowl shade. */
  private colourCell(g: Grid, k: number, colors: Float32Array | null): void {
    const k3 = k * 3, d = g.dark[k], vi = g.vtx[k] * 3;
    if (colors && vi >= 0) { g.col[k3] = colors[vi] * d; g.col[k3 + 1] = colors[vi + 1] * d; g.col[k3 + 2] = colors[vi + 2] * d; }
    else { g.col[k3] = 0.36 * d; g.col[k3 + 1] = 0.54 * d; g.col[k3 + 2] = 0.42 * d; }
  }

  /** A row of normals, the mesh's and the fine ground's, by central differences over the grid (outward). */
  private normalsRow(g: Grid, origin: THREE.Vector3, planet: PlanetState, i: number): void {
    for (let j = 0; j < this.n; j++) this.normalsAt(g, origin, planet, i, j);
  }

  /** A cell's two normals, by central differences over its four neighbours (clamped at the square's edge), outward. */
  private normalsAt(g: Grid, origin: THREE.Vector3, planet: PlanetState, i: number, j: number): void {
    const n = this.n;
    const i0 = Math.max(0, i - 1), i1 = Math.min(n - 1, i + 1), j0 = Math.max(0, j - 1), j1 = Math.min(n - 1, j + 1);
    const a = (i1 * n + j) * 3, b = (i0 * n + j) * 3, c = (i * n + j1) * 3, d = (i * n + j0) * 3, k3 = (i * n + j) * 3;
    for (let pass = 0; pass < 2; pass++) {
      const src = pass ? g.fine : g.pos, out = pass ? g.nrm : g.nrmCoarse;
      const ux = src[a] - src[b], uy = src[a + 1] - src[b + 1], uz = src[a + 2] - src[b + 2];
      const vx = src[c] - src[d], vy = src[c + 1] - src[d + 1], vz = src[c + 2] - src[d + 2];
      let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
      // outward: along the radial (the point relative to the planet's centre)
      const rx = src[k3] + origin.x - planet.position.x, ry = src[k3 + 1] + origin.y - planet.position.y, rz = src[k3 + 2] + origin.z - planet.position.z;
      const s = nx * rx + ny * ry + nz * rz < 0 ? -1 : 1;
      const l = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
      nx *= s / l; ny *= s / l; nz *= s / l;
      out[k3] = nx; out[k3 + 1] = ny; out[k3 + 2] = nz;
    }
  }

  /** The drawn patch's cells into the geometry (all of them: a build's swap, a scroll's). */
  private upload(g: Grid): void {
    const geo = this.mesh.geometry;
    const put = (name: string, a: Float32Array) => { const attr = geo.getAttribute(name) as THREE.BufferAttribute; (attr.array as Float32Array).set(a); attr.needsUpdate = true; };
    put('position', g.pos); put('normal', g.nrm); put('normalCoarse', g.nrmCoarse);
    put('fineLift', g.lift); put('edgeK', g.edge); put('color', g.col);
  }

  private swapIn(job: Build, amplitude: number): void {
    this.upload(job);
    this.mesh.position.copy(job.origin);
    this.mesh.visible = true;
    // (71.3 (b): the build's buffers become the drawn patch's, kept for a scroll; the old ones the next build's)
    const drawn: Grid = { pos: job.pos, fine: job.fine, lift: job.lift, edge: job.edge, col: job.col, nrm: job.nrm, nrmCoarse: job.nrmCoarse, vtx: job.vtx, dark: job.dark };
    this.buffers = this.kept ?? this.grid();
    this.kept = drawn;
    this.built = { planetId: job.planet.id, heightmap: job.planet.heightmap, anchor: job.anchor, ic: job.ic, jc: job.jc, amplitude, origin: job.origin, mask: job.mask, floor: job.floor, discs: job.discs, craterlets: job.planet.craterlets, digs: job.planet.digs };
    this.lastBuild = { ms: this.pendingMs, frames: this.pendingFrames };
    this.pending = null;
  }

  /**
   * 71.3 (b) (ARCHITECTURE §5.36 (4b), the owner's "seamless detail by distance"): the drawn patch moved to lattice point
   * (ic, jc) as a clipmap scrolls — its kept cells shifted (the lattice is the world's, so a cell two patches share is the
   * same numbers), only the rows and columns new to it filled, and the normals redone only where a neighbour is new or the
   * square's edge now clamps them. A few percent of a build, done in the frame: no time-sliced rebuild to fall behind at
   * speed, no restart, no whole new patch swapped in. The colours are refreshed from the planet's live ones for every cell.
   */
  private scroll(planet: PlanetState, ic: number, jc: number, ps: PatchPersonSet, colors: Float32Array | null): void {
    const t0 = performance.now();
    const b = this.built!, K = this.kept!, S = this.buffers!, n = this.n, di = ic - b.ic, dj = jc - b.jc;
    const at = { planet, anchor: b.anchor, origin: b.origin };
    const flat = this.flatMask(planet, b.anchor, ic, jc, b.discs);   // (the props' discs: the same discs, so a kept cell's share is its own)
    const fresh = this.fresh ??= new Uint8Array(n * n);
    fresh.fill(1);
    const jFrom = Math.max(0, -dj), jTo = Math.min(n, n - dj);   // the columns kept: source column j + dj in the square
    for (let i = 0; i < n; i++) {
      const si = i + di;
      if (si < 0 || si >= n || jFrom >= jTo) continue;
      const to = i * n + jFrom, from = si * n + jFrom + dj, len = jTo - jFrom;
      for (const key of ['pos', 'fine', 'nrm', 'nrmCoarse'] as const) S[key].set(K[key].subarray(from * 3, (from + len) * 3), to * 3);
      S.lift.set(K.lift.subarray(from, from + len), to); S.vtx.set(K.vtx.subarray(from, from + len), to); S.dark.set(K.dark.subarray(from, from + len), to);
      fresh.fill(0, to, to + len);
    }
    S.edge.set(K.edge);   // (the square's taper is the cell's place in it: the same for every patch)
    let filled = 0;
    for (let k = 0; k < n * n; k++) if (fresh[k]) { this.fillCell(S, at, (k / n) | 0, k % n, ic, jc, flat.mask, flat.floor, ps, colors); filled++; }
    for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
      const k = i * n + j;
      if (fresh[k] || i === 0 || j === 0 || i === n - 1 || j === n - 1 || fresh[k - n] || fresh[k + n] || fresh[k - 1] || fresh[k + 1]) this.normalsAt(S, b.origin, planet, i, j);
    }
    for (let k = 0; k < n * n; k++) this.colourCell(S, k, colors);
    this.buffers = K; this.kept = S;
    this.upload(S);
    this.built = { ...b, ic, jc, mask: flat.mask, floor: flat.floor };
    this.lastBuild = { ms: performance.now() - t0, frames: 1 };
    this.lastScroll = { ms: this.lastBuild.ms, cells: filled };
  }

  /**
   * S3: the ground radius this patch draws at `dir` — the fine ground near the focus, fading to the mesh with distance
   * from it exactly as the shader lifts the vertices (and to the mesh at the patch's own edge), the mesh beyond the
   * patch (or with no patch built on this planet). 67.50 (3): the mesh as drawn — lifted by the far ground's lift. The host stands drawn people on it, so nobody floats over the mesh
   * past the fade or sinks into a drawn hummock.
   */
  drawnGround(planet: PlanetState, dir: Vec3, ps: GroundDials): number {
    // 67.50 (3): the mesh as drawn — the far lift × (1 − the fade²) (rendering/groundLift.ts meshLift: none where the
    // relief is whole, none with no patch drawn)
    const coarse = groundAt(planet, dir, 'coarse', COARSE);
    const l = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z);
    if (l < 1e-12) return coarse;
    const px = planet.position.x + (dir.x / l) * coarse, py = planet.position.y + (dir.y / l) * coarse, pz = planet.position.z + (dir.z / l) * coarse;
    const far = coarse + meshLift({ x: px, y: py, z: pz });
    const b = this.built;
    if (!b || b.planetId !== planet.id || !(ps.fineAmplitude > 0) || !this.mesh.visible) return planet.stands?.length && ps.fineAmplitude > 0 ? standGround(planet.stands, planet.radius, dir.x / l, dir.y / l, dir.z / l, far, planet.standCells) : far;
    const { c, t1, t2, step } = b.anchor;
    const along = (dir.x * c.x + dir.y * c.y + dir.z * c.z) / l;
    if (along <= 0) return far;   // (the far side: no deck drawn there)
    // the lattice: a vertex at (i, j) is ∝ c + step·((ic − h + i)·t1 + (jc − h + j)·t2)
    const i = (dir.x * t1.x + dir.y * t1.y + dir.z * t1.z) / l / along / step - (b.ic - this.h);
    const j = (dir.x * t2.x + dir.y * t2.y + dir.z * t2.z) / l / along / step - (b.jc - this.h);
    const n = this.n;
    if (!(i >= 0 && i <= n - 1 && j >= 0 && j <= n - 1)) return planet.stands?.length ? standGround(planet.stands, planet.radius, dir.x / l, dir.y / l, dir.z / l, far, planet.standCells) : far;
    const edge = Math.min(1, Math.min(i, n - 1 - i, j, n - 1 - j) / this.edgeSteps);
    const dx = px - this.focus.x, dy = py - this.focus.y, dz = pz - this.focus.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const { inner, outer } = this.fadeRadii(ps);
    const t = edge * fade(d, inner, outer) * FINE_FADE.uFineOn.value;   // (× on: the height fade, as the shader's fineFade)
    // 67.39: the relief rises with the fade's square, as the shader lifts it; 67.50 (1): × the mask, as the vertices take it
    if (!(t > 0)) return planet.stands?.length ? standGround(planet.stands, planet.radius, dir.x / l, dir.y / l, dir.z / l, far, planet.standCells) : far;
    const i0 = Math.min(n - 2, Math.floor(i)), j0 = Math.min(n - 2, Math.floor(j)), fi = i - i0, fj = j - j0, mk = b.mask, fl = b.floor;
    const m = (mk[i0 * n + j0] * (1 - fj) + mk[i0 * n + j0 + 1] * fj) * (1 - fi) + (mk[(i0 + 1) * n + j0] * (1 - fj) + mk[(i0 + 1) * n + j0 + 1] * fj) * fi;
    // 67.52: the floor a seated build's disc is flattened to (×(1 − its vertices' mask), as the vertices take it)
    const flo = ((fl[i0 * n + j0] * (1 - mk[i0 * n + j0])) * (1 - fj) + (fl[i0 * n + j0 + 1] * (1 - mk[i0 * n + j0 + 1])) * fj) * (1 - fi) + ((fl[(i0 + 1) * n + j0] * (1 - mk[(i0 + 1) * n + j0])) * (1 - fj) + (fl[(i0 + 1) * n + j0 + 1] * (1 - mk[(i0 + 1) * n + j0 + 1])) * fj) * fi;
    // the vertices' own: the far lift × (1 − t²) and the relief × the mask × t² (the shader's, edge and all)
    const L = GROUND_LIFT.uGroundLift.value * FINE_FADE.uFineOn.value;
    // (209's follow-up: the ground drawn without the walkable tops, then a deck where it stands higher — the people on it drawn on it)
    const g = coarse + L * (1 - t * t) + ((groundAt(planet, dir, 'ground', ps) - coarse) * m + flo) * t * t;
    return planet.stands?.length ? standGround(planet.stands, planet.radius, dir.x / l, dir.y / l, dir.z / l, g, planet.standCells) : g;
  }

  /**
   * 67.50 (1): the relief's share kept at each vertex of a build round (ic, jc): 1, but within a prop's disc and its margin
   * (rendering/fineFlatten.ts flatWeight, the least of the discs near). Each disc is rasterised over the vertices its
   * reach covers (gnomonic coordinates on the anchor), so the cost is the props', not the patch's.
   */
  private flatMask(planet: PlanetState, a: Anchor, ic: number, jc: number, discs: readonly FlatDisc[] | null): { mask: Float32Array; floor: Float32Array } {
    const n = this.n, h = this.h, mask = new Float32Array(n * n).fill(1), floor = new Float32Array(n * n);
    if (!discs?.length) return { mask, floor };
    const R = planet.radius, margin = Math.max(0, this.flatMargin);
    const v = { x: 0, y: 0, z: 0 };
    for (const q of discs) {
      const along = q.dir.x * a.c.x + q.dir.y * a.c.y + q.dir.z * a.c.z;
      if (along <= 0.9) continue;
      const qi = (q.dir.x * a.t1.x + q.dir.y * a.t1.y + q.dir.z * a.t1.z) / along / a.step - (ic - h);
      const qj = (q.dir.x * a.t2.x + q.dir.y * a.t2.y + q.dir.z * a.t2.z) / along / a.step - (jc - h);
      const rs = (q.r + margin) / (R * a.step) + 1;
      if (qi < -rs || qj < -rs || qi > n - 1 + rs || qj > n - 1 + rs) continue;
      for (let i = Math.max(0, Math.floor(qi - rs)); i <= Math.min(n - 1, Math.ceil(qi + rs)); i++) {
        const gi = (ic - h + i) * a.step;
        for (let j = Math.max(0, Math.floor(qj - rs)); j <= Math.min(n - 1, Math.ceil(qj + rs)); j++) {
          const gj = (jc - h + j) * a.step;
          v.x = a.c.x + a.t1.x * gi + a.t2.x * gj; v.y = a.c.y + a.t1.y * gi + a.t2.y * gj; v.z = a.c.z + a.t1.z * gi + a.t2.z * gj;
          const l = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
          const dx = v.x / l - q.dir.x, dy = v.y / l - q.dir.y, dz = v.z / l - q.dir.z;
          const w = flatWeight(Math.sqrt(dx * dx + dy * dy + dz * dz) * R, q.r, margin), k = i * n + j;
          if (w < mask[k]) { mask[k] = w; floor[k] = q.lift ?? 0; }
        }
      }
    }
    return { mask, floor };
  }


  /** Vertices in the patch (n × n). */
  get vertexCount(): number { return this.n * this.n; }

  dispose(): void {
    FINE_FADE.uFineOn.value = 0;
    this.scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
